import {
  DetailedScenario,
  ScenarioActivityStatus,
  UpdateScenarioRequest as UpdateScenarioRequestV2,
} from "@mutiny-group/sbo-openapi-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { debounce } from "lodash";
import { useCallback, useRef } from "react";

import { components } from "@/@types/api";
import { INSIGHTS_BACKEND_URL } from "@/lib/axios";
import { MutationConfig } from "@/lib/react-query";
import { tenantIdStorage } from "@/storage";
import { axiosApi } from "@/Utils";

import { useScenarioBuilderClient } from "../../api/hooks/useScenarioBuilderClient";
import { ScenarioTypeEnum, StatusEnum } from "../../types";
import { updateScenarioV2 } from "./v2/updateScenario";

type Scenario = components["schemas"]["NewUpdateScenarioResponse"];
type UpdateScenarioRequest = components["schemas"]["NewUpdateScenarioRequestV2"];

type UpdateScenarioParams = {
  scenarioUUID: string;
  scenario: UpdateScenarioRequest;
  scenarioType?: string;
};

export const updateScenario = ({
  scenarioUUID,
  scenario,
}: UpdateScenarioParams): Promise<Scenario> => {
  return axiosApi.put(`/api/v2/scenarios-new/${scenarioUUID}/v2`, scenario, {
    baseURL: INSIGHTS_BACKEND_URL,
  });
};

type UseUpdateScenarioOptions = {
  config?: MutationConfig<typeof updateScenario | typeof updateScenarioV2>;
  debounceMs?: number;
  isScenariosApiEnabled?: boolean;
};

export const useUpdateScenarioV3 = ({
  config,
  debounceMs = 500,
  isScenariosApiEnabled = false,
}: UseUpdateScenarioOptions = {}) => {
  const queryClient = useQueryClient();
  const debouncedFnRef = useRef<ReturnType<typeof debounce> | null>(null);
  const scenarioBuilderClient = useScenarioBuilderClient();

  const updateScenarioFn = useCallback(
    ({ scenarioUUID, scenario, scenarioType }: UpdateScenarioParams) => {
      if (isScenariosApiEnabled) {
        return updateScenarioV2({
          scenarioBuilderClient,
          scenarioUUID,
          scenario: convertScenarioToDetailedScenario(scenario, scenarioType),
        });
      }
      return updateScenario({ scenarioUUID, scenario });
    },
    [isScenariosApiEnabled, scenarioBuilderClient]
  );

  // Create a debounced version of the update function that returns a Promise
  const debouncedUpdateScenario = useCallback(
    async ({ scenarioUUID, scenario, scenarioType }: UpdateScenarioParams): Promise<Scenario> => {
      return new Promise((resolve, reject) => {
        // Cancel any pending debounced call
        if (debouncedFnRef.current) {
          debouncedFnRef.current.cancel();
        }

        // Create new debounced function
        debouncedFnRef.current = debounce(async () => {
          try {
            const result = await updateScenarioFn({ scenarioUUID, scenario, scenarioType });
            if (isScenariosApiEnabled) {
              resolve(convertDetailedScenarioToScenario(result as DetailedScenario));
            } else {
              resolve(result as Scenario);
            }
          } catch (error) {
            reject(error);
          }
        }, debounceMs);

        // Execute the debounced function
        debouncedFnRef.current();
      });
    },
    [debounceMs, updateScenarioFn, isScenariosApiEnabled]
  );

  return useMutation({
    onMutate: async (updatingScenario) => {
      if (isScenariosApiEnabled) {
        await queryClient.cancelQueries({
          queryKey: ["scenario-v2", updatingScenario.scenarioUUID, scenarioBuilderClient],
        });
      } else {
        await queryClient.cancelQueries({ queryKey: ["scenario", updatingScenario.scenarioUUID] });
      }
    },
    onSuccess: async (updatedScenario) => {
      if (isScenariosApiEnabled) {
        await queryClient.invalidateQueries({ queryKey: ["scenarios-v2", scenarioBuilderClient] });
        await queryClient.invalidateQueries({
          queryKey: ["scenario-v2", updatedScenario.uuid, scenarioBuilderClient],
        });
        await queryClient.invalidateQueries({
          queryKey: ["scenario-results-v2", updatedScenario.uuid, scenarioBuilderClient],
        });
        await queryClient.invalidateQueries({
          queryKey: ["activity-log-v2", updatedScenario.uuid, scenarioBuilderClient],
        });
        await queryClient.invalidateQueries({
          queryKey: ["analyse-allocation-strategy", updatedScenario.uuid, scenarioBuilderClient],
        });
      } else {
        await queryClient.invalidateQueries({ queryKey: ["scenarios-new"] });
        await queryClient.invalidateQueries({ queryKey: ["scenario", updatedScenario.uuid] });
        await queryClient.invalidateQueries({ queryKey: ["activity-logs", updatedScenario.uuid] });
        await queryClient.invalidateQueries({ queryKey: ["is-optimizer-running"] });
      }
    },
    ...config,
    mutationFn: debouncedUpdateScenario,
  });
};

const convertScenarioToDetailedScenario = (
  scenario: UpdateScenarioRequest,
  scenarioType?: string
): UpdateScenarioRequestV2 => {
  const baseScenario = {
    name: scenario.name,
    description: scenario.description,
    startDate: scenario.startDate ? new Date(scenario.startDate) : null,
    endDate: scenario.endDate ? new Date(scenario.endDate) : null,
    scopeBrand: scenario.brand === "default" ? null : scenario.brand,
    scopeGeographies: scenario.geography ? [scenario.geography] : null,
    totalBudget: scenario.value,
    activityStatus: (function () {
      if (scenario.scenarioStatus === "completed") return ScenarioActivityStatus.ReadyForReview;
      return scenario.scenarioStatus as ScenarioActivityStatus;
    })(),
  };

  if (!scenario.scenarioConstraints || !scenarioType) return baseScenario;

  if (scenarioType === "optimizer") {
    return {
      ...baseScenario,
      optimizerSpendConstraints: scenario.scenarioConstraints.map((constraint) => ({
        minSpend: constraint.minValue || 0,
        maxSpend: constraint.maxValue || 0,
        channel: constraint.channel,
        geography: constraint.geography ?? null,
        marketingSpendNode: {
          customer: tenantIdStorage.get() || "",
          brand: constraint.brand || "",
          category: constraint.category || "",
          product: constraint.product || "",
        },
      })),
    };
  }

  if (scenarioType === "budget") {
    return {
      ...baseScenario,
      forecasterMarketingSpends: scenario.scenarioConstraints.map((constraint) => ({
        geography: constraint.geography ?? null,
        channel: constraint.channel || "",
        value: constraint.maxValue || 0,
        spend: constraint.maxValue || 0,
        marketingSpendNode: {
          customer: tenantIdStorage.get() || "",
          brand: constraint.brand || "",
          category: constraint.category || "",
          product: constraint.product || "",
        },
      })),
    };
  }

  return baseScenario;
};

const convertDetailedScenarioToScenario = (scenario: DetailedScenario): Scenario => {
  const baseScenario = {
    uuid: scenario.uuid,
    name: scenario.name || "",
    brand: scenario.request.scope?.brand ?? "default",
    description: scenario.description || "",
    value: scenario.totalBudget || 0,
    startDate: scenario.request.startDate || "",
    endDate: scenario.request.endDate || "",
    scenarioType: scenario.scenarioDisplayType as ScenarioTypeEnum,
    createdAt: scenario.createdAt?.toISOString() || "",
    updatedAt: "",
    deletedAt: scenario.deletedAt?.toISOString() || "",
    createdBy: scenario.userId || "",
    scenarioStatus: scenario.status as StatusEnum,
    statusMessage: "",
    geography: scenario.request.scope?.geographies?.[0] ?? null,
  };

  if (scenario.scenarioDisplayType === "optimizer") {
    return {
      ...baseScenario,
      scenarioConstraints: scenario.optimizerSpendConstraints?.map((constraint) => ({
        brand: constraint.marketingSpendNode.brand ?? null,
        category: constraint.marketingSpendNode.category ?? null,
        product: constraint.marketingSpendNode.product ?? null,
        scenarioUuid: scenario.uuid || "",
        channel: constraint.channel ?? "",
        minValue: constraint.minSpend ?? 0,
        maxValue: constraint.maxSpend ?? 0,
        geography: constraint.geography ?? null,
      })),
    };
  }

  if (scenario.scenarioDisplayType === "budget") {
    return {
      ...baseScenario,
      scenarioConstraints: scenario.forecasterMarketingSpends?.map((constraint) => ({
        brand: constraint.marketingSpendNode.brand ?? null,
        category: constraint.marketingSpendNode.category ?? null,
        product: constraint.marketingSpendNode.product ?? null,
        scenarioUuid: scenario.uuid || "",
        channel: constraint.channel ?? "",
        minValue: 0,
        maxValue: constraint.spend || 0,
        geography: constraint.geography ?? null,
      })),
    };
  }

  return baseScenario;
};
